import { Newspaper, MapPinned, Sparkles, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal, Eyebrow } from "./Reveal";
import { MEDIA_LOGOS } from "../lib/mediaLogos";

/* Homepage shows only major publications — full list lives on /media */
const MAJOR_LOGOS = MEDIA_LOGOS.filter((l) => !l.img.includes("/all/"));

export const MediaCoverage = () => (
    <section id="media" className="relative scroll-mt-20 overflow-hidden py-20 lg:py-28" data-testid="media-coverage-section">
        <div className="glow-orb right-[15%] top-0 h-[320px] w-[420px] bg-cyan-500/10" />
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <Reveal className="text-center">
                <Eyebrow>PR News & Media Coverage</Eyebrow>
                <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl" data-testid="media-heading">
                    WorkAssign AI in the <span className="cyan-gradient-text">news</span>
                </h2>
                <p className="mx-auto mt-4 max-w-2xl text-slate-400">
                    Our initiative to bring AI automation to small businesses made headlines across the country.
                </p>
            </Reveal>

            <Reveal delay={0.1}>
                <div className="mx-auto mt-8 flex max-w-3xl flex-wrap items-center justify-center gap-3" data-testid="media-stats">
                    {[
                        { icon: Newspaper, label: `${MEDIA_LOGOS.length} Publications` },
                        { icon: MapPinned, label: "Pan-India Coverage" },
                        { icon: Sparkles, label: "September 2026 Spotlight" },
                    ].map((s) => (
                        <span
                            key={s.label}
                            className="flex items-center gap-2 rounded-full border border-cyan-500/25 bg-cyan-500/10 px-4 py-2 text-xs font-semibold text-cyan-300"
                        >
                            <s.icon className="h-3.5 w-3.5" /> {s.label}
                        </span>
                    ))}
                </div>
            </Reveal>
        </div>

        {/* ---- Sliding logo marquee — all publications ---- */}
        <Reveal delay={0.15}>
            <div className="marquee-mask mt-14 overflow-hidden" data-testid="media-grid">
                <div
                    className="animate-marquee flex w-max items-center gap-14 pr-14 sm:gap-20 sm:pr-20"
                    style={{ animationDuration: "150s" }}
                >
                    {[...MAJOR_LOGOS, ...MAJOR_LOGOS].map((l, i) => (
                        <a
                            key={`${l.name}-${i}`}
                            href={l.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label={`${l.name} — read article`}
                            data-testid={i < MAJOR_LOGOS.length ? `media-card-${i}` : undefined}
                            className="group flex shrink-0 items-center justify-center transition-transform hover:scale-110"
                        >
                            <img
                                src={l.img}
                                alt={`${l.name} logo`}
                                loading="lazy"
                                data-testid={i < MAJOR_LOGOS.length ? `media-logo-${i}` : undefined}
                                className={`h-16 w-auto max-w-[170px] object-contain sm:h-20 sm:max-w-[210px] ${
                                    l.bright
                                        ? "[filter:drop-shadow(0_0_14px_rgba(255,255,255,0.55))_drop-shadow(0_0_34px_rgba(255,255,255,0.30))]"
                                        : "[filter:drop-shadow(0_10px_24px_rgba(0,0,0,0.5))_drop-shadow(0_0_18px_rgba(255,255,255,0.14))]"
                                }`}
                            />
                        </a>
                    ))}
                </div>
            </div>
        </Reveal>

        <p className="mt-10 text-center text-xs text-slate-600" data-testid="media-count-note">
            {MEDIA_LOGOS.length} publications covered WorkAssign AI — hover to pause, click a logo to read the article
        </p>
        <div className="mt-5 text-center">
            <Link
                to="/media"
                data-testid="media-view-all-link"
                className="inline-flex items-center gap-2 rounded-full border border-cyan-500/30 bg-cyan-500/10 px-7 py-3 text-sm font-semibold text-cyan-300 transition-colors hover:bg-cyan-500/20"
            >
                View All Media Coverage <ArrowRight className="h-4 w-4" />
            </Link>
        </div>
    </section>
);
